import { z } from 'zod';
import { MomentState } from './moment.enums';

/**
 * Host go-live request. All fields optional — a moment can go live
 * untitled. Any additional metadata (cover, tags) is out of scope for
 * MVP; add fields here as the product surface grows.
 */
export const GoLiveMomentRequestSchema = z.object({
  title: z.string().max(120).optional(),
});

/**
 * Broadcaster payload — everything the phone needs to point its
 * RTMP(S) publisher at the newly-provisioned channel. `streamKey` is
 * plaintext and MUST be treated as secret on the client (never log,
 * never render, never persist). Returned only on go-live and never
 * re-fetched.
 */
export const GoLiveMomentResponseSchema = z.object({
  momentId: z.uuid(),
  state: z.literal(MomentState.LIVE),
  liveStartedAt: z.iso.datetime(),
  ingestEndpoint: z.url(),
  streamKey: z.string(),
  playbackUrl: z.url(),
});

/**
 * Host live-console read for a moment. `playbackUrl` here is the raw
 * (unsigned) URL — hosts don't need a signed ticket to preview their
 * own stream. Viewer playback goes through the viewer endpoint.
 */
export const HostMomentLivePayloadSchema = z.object({
  momentId: z.uuid(),
  state: z.enum(MomentState),
  liveStartedAt: z.iso.datetime(),
  liveEndedAt: z.iso.datetime().nullable(),
  viewers: z.number().int().nonnegative(),
  peakViewers: z.number().int().nonnegative(),
  playbackUrl: z.url().nullable(),
  serverNow: z.iso.datetime(),
});

/**
 * Authenticated viewer playback payload. Access is gated on a valid
 * session — no anonymous consumers — but there is NO registration /
 * entitlement check. Any logged-in user with the moment link can watch.
 * On ENDED state the playback fields collapse to null and the FE
 * renders an empty state (rather than 404) so a shared link stays
 * informative after the host ends the stream.
 */
export const ViewerMomentPayloadSchema = z.object({
  momentId: z.uuid(),
  state: z.enum(MomentState),
  title: z.string().nullable(),
  playbackUrl: z.url().nullable(),
  token: z.string().nullable(),
  tokenExpiresAt: z.iso.datetime().nullable(),
  viewers: z.number().int().nonnegative(),
  liveStartedAt: z.iso.datetime(),
  liveEndedAt: z.iso.datetime().nullable(),
});

/**
 * Start a viewing session on a moment. Same shape as the Event
 * flow — the FE POSTs this once when the player mounts, receives a
 * server-issued `sessionId`, then opens the Socket.IO connection at
 * `/moment-viewing-session?sessionId=<id>` and emits `heartbeat` every
 * ~30s. On teardown it calls the `end` endpoint.
 */
export const StartMomentViewingSessionRequestSchema = z.object({
  deviceType: z.string().max(50).optional(),
});

export const StartMomentViewingSessionResponseSchema = z.object({
  sessionId: z.string(),
});

export const EndMomentViewingSessionResponseSchema = z.object({
  durationSec: z.number().int().min(0),
});

/**
 * WebSocket ACK for a `heartbeat` message. Mirrors the Event flow so
 * FE state machines can share a client. `reason` is set only when
 * `ok === false`; the socket is closed by the server on the same tick.
 */
export const MomentViewingSessionHeartbeatAckSchema = z.object({
  ok: z.boolean(),
  reason: z.enum(['unauthorized', 'session_ended', 'not_owner', 'internal']).optional(),
});

/** Client → server frame names on `/moment-viewing-session`. */
export const MomentViewingSessionEvents = {
  Heartbeat: 'heartbeat',
} as const;
